import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Types
import { ItemType } from 'Shared/types/MeetingItem.type';
import { User } from 'Shared/constants/types';

// Utils
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';
import { convertToMoment, convertMomentToUnix } from 'Shared/utils/DateTime.utils';

// Actions
import { createNewMeetingItem } from 'Shared/actions/MeetingItems.actions';

// Utils
import { getTypeHumanReadable, isActionItem } from 'Shared/utils/MeetingItem.utils';
import { createRichTextEditorState, editorStateToJsonString } from 'Wpds/components/rich-text/utils/EditorState.utils';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Components
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import { WpFormLayout } from 'Wpds/components/form';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WorkspaceSelect from 'Shared/components/WorkspaceSelect/WorkspaceSelect';
import WpUserInput, { WidthType } from 'Wpds/components/user-input/WpUserInput';
import { WpCheckbox } from 'Wpds/components/checkbox';
import WpDayPickerInput from 'Wpds/components/day-picker/WpDayPickerInput';

// Styles
import './CreateAiOrDtModal.scss';

export { ItemType };

type Props = {
  isOpen: boolean;
  toggleIsOpen: () => void;
  itemType: ItemType;
  workspaceUuid?: string;
};

const CreateAiOrDtModal = ({ isOpen, toggleIsOpen, itemType, workspaceUuid = null }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const workspaces = useSelector((state: any) => state.workspaces);

  const [text, setText] = useState('');
  const [workspaceValue, setWorkspaceValue] = useState(workspaceUuid);
  const [workspace, setWorkspace] = useState(null);
  const [dueDate, setDueDate] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [isCreateAnotherChecked, setIsCreateAnotherChecked] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [resetOwnerSelect, toggleResetOwnerSelect] = useToggle(true);
  const [isResettingForm, toggleIsResettingForm] = useToggle(true);
  const [isSubmitting, toggleIsSubmitting] = useToggle(false);

  const humanReadableType = getTypeHumanReadable(itemType);
  const { tr } = useTranslation();
  const isTypeActionItem = isActionItem(itemType);
  const { id: currentUserId } = useCurrentUser();

  const resetForm = () => {
    setText('');
    setWorkspace(null);
    setSelectedOwner(null);

    toggleIsResettingForm();
    setTimeout(() => toggleIsResettingForm(), 10);
  };

  const handleSelectWorkspace = workspace => {
    setWorkspaceValue(workspace.id);
    setWorkspace(workspace);
    setSelectedOwner(null);
    setUsers(workspace.users);
  };

  const handleSubmitButtonClick = () => {
    if (!text || !workspace) return;

    toggleIsSubmitting();

    const defaultDueBy = dueDate
      ? convertMomentToUnix(convertToMoment(dueDate).startOf('day').startOf('hour').add(12, 'hours'))
      : null;

    const data = {
      uuid: uuidv4(),
      item_type: itemType,
      body: editorStateToJsonString(createRichTextEditorState(text)),
      workspace_id: workspace.id,
      position: 0,
      dueBy: defaultDueBy,
      assignment_attributes: selectedOwner
        ? {
            user_id: currentUserId,
            person_id: selectedOwner?.personId,
          }
        : null,
    };

    dispatch(createNewMeetingItem(data, 'workspace')).then(response => {
      WpToastManager.success(
        isTypeActionItem
          ? tr('perform.workspace.create_ai_or_dt_modal.item_created')
          : tr('perform.workspace.create_ai_or_dt_modal.topic_created'),
        {
          actionBtnLabel: tr('common.view'),
          actionBtnFnc: () => {
            history.push(
              PageUrlsUtils.makeWorkspaceMeetingItemCommentsUrl(
                response.data.data.attributes.workspaceId,
                response.data.data.attributes.uuid
              )
            );
          },
        }
      );

      toggleIsSubmitting();
      if (isCreateAnotherChecked) {
        setText('');
      } else {
        resetForm();
        toggleIsOpen();
      }
    });
  };

  useEffect(() => {
    if (users.length > 0) {
      toggleResetOwnerSelect();
      setTimeout(() => toggleResetOwnerSelect(), 10);
    }
  }, [workspace]);

  useEffect(() => {
    if (!isOpen) resetForm();
  }, [isOpen]);

  useEffect(() => {
    if (workspaceUuid) {
      handleSelectWorkspace(workspaces.find(ws => ws.id === workspaceUuid));
    }
  }, []);

  const baseClassName = 'CreateAiOrDtModal';

  return (
    <WpModal isOpen={isOpen} onToggle={toggleIsOpen} size="sm" dismissOnBackdrop={false} zIndex={1060}>
      <WpModalHeader>{tr('perform.workspace.create_ai_or_dt_modal.title', humanReadableType)}</WpModalHeader>
      <WpModalBody>
        {tr('perform.workspace.create_ai_or_dt_modal.body', humanReadableType)}
        {isResettingForm && (
          <div className={`${baseClassName}__form`}>
            <WpFormLayout>
              <WpTextField
                id="quick-add-item-body"
                label={
                  isTypeActionItem
                    ? tr('perform.workspace.create_ai_or_dt_modal.item_label')
                    : tr('perform.workspace.create_ai_or_dt_modal.topic_label')
                }
                placeholder={
                  isTypeActionItem
                    ? tr('perform.workspace.create_ai_or_dt_modal.item_placeholder')
                    : tr('perform.workspace.create_ai_or_dt_modal.topic_placeholder')
                }
                prefixIcon="check-circle"
                autoFocus
                value={text}
                onChange={setText}
                labelHidden
                disabled={isSubmitting}
              />
            </WpFormLayout>
            {isTypeActionItem && (
              <WpFormLayout>
                <WpDayPickerInput
                  id="create-dt-or-ai-date-input"
                  label={tr('perform.common.dates.due_by')}
                  placeholder={tr('perform.common.dates.select_due_date')}
                  selected={dueDate}
                  onSelect={setDueDate}
                  disabled={isSubmitting}
                />
              </WpFormLayout>
            )}
            <WpFormLayout>
              <WorkspaceSelect
                label={tr('perform.common.workspace.label')}
                id="create-dt-or-ai-workspace-select"
                workspaces={workspaces}
                onChange={handleSelectWorkspace}
                placeholder={tr('perform.common.workspace.select_workspace')}
                value={workspaceValue}
                disabled={isSubmitting}
              />
            </WpFormLayout>
            <WpFormLayout>
              {resetOwnerSelect && (
                <WpUserInput
                  label={tr('perform.common.owner.label')}
                  id="create-dt-or-ai-owner-select"
                  users={users}
                  optionsWidth={WidthType.target}
                  showSearch={users.length > 5}
                  placeholder={tr('perform.common.owner.select_owner')}
                  value={selectedOwner}
                  onChange={setSelectedOwner}
                  disabled={isSubmitting}
                />
              )}
            </WpFormLayout>
          </div>
        )}
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <div className={`${baseClassName}__create-another-cb`}>
            <WpCheckbox
              id="create-another"
              label={tr('perform.common.create_another')}
              checked={isCreateAnotherChecked}
              onChange={setIsCreateAnotherChecked}
            />
          </div>
          <WpButton variant="tertiary" onClick={toggleIsOpen}>
            {tr('perform.common.cancel')}
          </WpButton>
          <WpButton loading={isSubmitting} onClick={handleSubmitButtonClick}>
            {tr('perform.common.create')}
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default CreateAiOrDtModal;
